"use client";

import { create } from "zustand";
import { useSessionStore } from "@/stores/sessionStore";
import { useAdminStore } from "@/stores/adminStore";
import { useMediaStore } from "@/stores/mediaStore";
import { auth } from "@/lib/firebase";
import { useEffect, useRef } from 'react';

interface WebSocketState {
  connections: Map<string, WebSocket>;
  reconnectAttempts: Map<string, number>;
  connect: (url: string) => WebSocket | null;
  disconnect: (url: string) => void;
  isConnected: (url: string) => boolean;
}

const getWebSocketToken = async (): Promise<string | undefined> => {
  try {
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken(true); // Force refresh token
      console.log(`Fetched fresh token for WebSocket: ${token.slice(0, 10)}...`);
      return token;
    } else {
      const { session } = useSessionStore.getState();
      const token = session?.user?.token;
      if (token) {
        console.log(`Using session token for WebSocket: ${token.slice(0, 10)}...`);
        return token;
      }
      console.warn("No authentication token available for WebSocket connection");
      return undefined;
    }
  } catch (error) {
    console.error("Failed to fetch token for WebSocket:", error);
    return undefined;
  }
};

export const useWebSocketStore = create<WebSocketState>((set, get) => ({
  connections: new Map<string, WebSocket>(),
  reconnectAttempts: new Map<string, number>(),

  connect: (url: string) => {
    if (typeof window === "undefined") return null; // SSR guard
    const existing = get().connections.get(url);
    if (existing && existing.readyState === WebSocket.OPEN) {
      console.log(`WebSocket to ${url} already open`);
      return existing;
    }

    const { session } = useSessionStore.getState();
    let token = session?.user?.token;
    if (!token && auth.currentUser) {
      console.warn("Using potentially stale token from session; consider refreshing");
    }
    if (!token) {
      console.warn(`No authentication token available for WebSocket connection to ${url}`);
      return null;
    }

    try {
      const separator = url.includes('?') ? '&' : '?';
      const wsUrl = `${url}${separator}token=${encodeURIComponent(token)}`;
      console.log(`Attempting WebSocket connection to: ${wsUrl}`);
      const ws = new WebSocket(wsUrl);

      const reconnectAttempts = get().reconnectAttempts.get(url) || 0;
      const maxAttempts = 5;
      const baseDelay = 5000; // 5 seconds

      ws.onopen = () => {
        console.log(`WebSocket to ${url} opened successfully`);
        set((state) => {
          const newConnections = new Map(state.connections);
          const newAttempts = new Map(state.reconnectAttempts);
          newConnections.set(url, ws);
          newAttempts.delete(url); // Reset attempts on success
          return { connections: newConnections, reconnectAttempts: newAttempts };
        });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`WebSocket message from ${url}:`, JSON.stringify(data, null, 2));
          if (url.includes("admin-updates")) {
            const { type, payload } = data;
            if (type === "user_updated") {
              console.log(`Processing user_updated: User ID ${payload.id}, userType: ${payload.userType}`);
              useAdminStore.getState().updateUser(payload);
            } else if (type === "metrics_updated") {
              console.log(`Processing metrics_updated: adminUsers: ${payload.adminUsers}`);
              useAdminStore.getState().setMetrics(payload);
            } else {
              console.warn(`Unknown WebSocket message type: ${type}`);
            }
          } else if (url.includes("media-updates")) {
            const { type, payload } = data;
            if (type === "media_updated" || type === "media_added") {
              console.log(`Processing ${type}: Media ID ${payload.id}`);
              useMediaStore.getState().updateMediaRecord(payload);
            } else if (type === "media_removed") {
              console.log(`Processing media_removed: Media ID ${payload.id}`);
              useMediaStore.getState().removeMediaRecord(payload.id);
            } else {
              console.warn(`Unknown WebSocket message type: ${type}`);
            }
          }
        } catch (error) {
          console.error(`Error parsing WebSocket message from ${url}:`, error, `Raw data:`, event.data);
        }
      };

      ws.onclose = async (event) => {
        console.log(`WebSocket to ${url} closed. Code: ${event.code}, Reason: ${event.reason}`);
        set((state) => {
          const newConnections = new Map(state.connections);
          const newAttempts = new Map(state.reconnectAttempts);
          newConnections.delete(url);
          if (event.code !== 1000 && reconnectAttempts < maxAttempts) {
            newAttempts.set(url, reconnectAttempts + 1);
          }
          return { connections: newConnections, reconnectAttempts: newAttempts };
        });
        if (event.code !== 1000 && reconnectAttempts < maxAttempts) {
          const delay = baseDelay * Math.pow(2, reconnectAttempts);
          console.log(`Reconnecting to ${url} in ${delay / 1000} seconds (attempt ${reconnectAttempts + 1}/${maxAttempts})...`);
          const newToken = await getWebSocketToken();
          if (newToken) {
            setTimeout(() => get().connect(url), delay);
          } else {
            console.error(`No token available for reconnection to ${url}, aborting`);
          }
        } else if (reconnectAttempts >= maxAttempts) {
          console.error(`Max reconnection attempts (${maxAttempts}) reached for ${url}. Giving up.`);
        }
      };

      ws.onerror = (error: Event) => {
        const wsError = error as ErrorEvent;
        console.error(`WebSocket to ${url} error:`, {
          message: wsError.message || "Unknown error",
          type: wsError.type,
          url: ws.url,
          readyState: ws.readyState,
          timestamp: new Date().toISOString(),
        });
      };

      return ws;
    } catch (error) {
      console.error(`Failed to initialize WebSocket to ${url}:`, error, `Timestamp: ${new Date().toISOString()}`);
      return null;
    }
  },

  disconnect: (url: string) => {
    const ws = get().connections.get(url);
    if (ws) {
      ws.close();
      set((state) => {
        const newConnections = new Map(state.connections);
        const newAttempts = new Map(state.reconnectAttempts);
        newConnections.delete(url);
        newAttempts.delete(url);
        return { connections: newConnections, reconnectAttempts: newAttempts };
      });
      console.log(`WebSocket to ${url} disconnected`);
    }
  },

  isConnected: (url: string) => {
    const ws = get().connections.get(url);
    return ws !== undefined && ws.readyState === WebSocket.OPEN;
  },
}));

export function useMediaWebSocket(token: string) {
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!apiUrl) {
      console.error("NEXT_PUBLIC_API_URL is not defined in environment variables");
      return; // Early return if apiUrl is undefined
    }
    const url = apiUrl.replace(/^https?:\/\//, "wss://") + `/media-updates?token=${encodeURIComponent(token)}`;
    console.log('Attempting WS to', url);

    // 1) Create socket
    const ws = new WebSocket(url);
    wsRef.current = ws;

    // 2) Hook up your normal handlers…
    ws.onopen = () => console.log('WS opened');
    ws.onmessage = (evt) => {
      console.log('WS message:', evt.data);
      // …your existing message logic…
    };

    // 3) FULL error logging
    ws.onerror = (err) => {
      console.error('⚠️ WS error event:', err);
    };

    // 4) FULL close logging
    ws.onclose = (ev) => {
      console.warn(`⚠️ WS closed: code=${ev.code}, reason="${ev.reason}"`);
    };

    // 5) Clean up on unmount
    return () => {
      console.log('Cleaning up WS');
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token]);

  return wsRef.current;
}